import type { FC, ReactNode, PropsWithChildren } from 'react'
import { useMemo } from 'react'

import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { DataIcon, BusinessIcon, SafeIcon, ReportIcon, CompliantIcon } from '@segment/matcha/components/Icons/Brandbook'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

type ColData = {
  icon: ReactNode
  heading: string
  description: string
}

const copy = {
  heading: `Segment’s commitment to data privacy`,
  description: `We take the responsibility of respecting privacy seriously. Here are a few initiatives Segment is committed to:`,
}

const CommitmentToDataPrivacy: FC<PropsWithChildren<unknown>> = () => {
  const theme = useTheme()

  const columnTrackData: ColData[] = useMemo(
    () => [
      {
        icon: <DataIcon color={theme.palette.twilio.gray[100]} />,
        heading: `Data Processing Agreement`,
        description: `Our Data Processing Agreement (DPA) reflects the requirements of the GDPR.`,
      },
      {
        icon: <BusinessIcon color={theme.palette.twilio.gray[100]} />,
        heading: `Data Transfer Practices`,
        description: `We offer Standard Contractual Clauses for compliant user data transfer and storage outside of the EU.`,
      },
      {
        icon: <SafeIcon color={theme.palette.twilio.gray[100]} />,
        heading: `Privacy by Design`,
        description: `Your data is yours to own. Segment does not sell our customers' user data.`,
      },
      {
        icon: <ReportIcon color={theme.palette.twilio.gray[100]} />,
        heading: `Privacy Policy`,
        description: `Our Privacy Policy honors CCPA, the GDPR, the Privacy Shield Framework.`,
      },
      {
        icon: <CompliantIcon color={theme.palette.twilio.gray[100]} />,
        heading: `Data Protection Officer`,
        description: `Segment has appointed a Data Protection Officer to oversee our ongoing compliance efforts.`,
      },
    ],
    [theme]
  )
  return (
    <Wrapper id="commitment-to-data-privacy">
      <SectionBase variant="centered">
        <SectionCopy>
          <CopyTextBase centered={true}>
            <CopyText.Heading>{copy.heading}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
          </CopyTextBase>
        </SectionCopy>
        <Section.Contents>
          <StyledColumnTrack columns={1} overrides={{ lg: 3 }} center={false}>
            {columnTrackData.map(({ heading, description, icon }) => (
              <FeatureBoxBase alignment="left" headerPosition="top" key={heading}>
                <FeatureBox.Header>
                  <IconWrapper elevation={1} variant="circle">
                    {icon}
                  </IconWrapper>
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title>{heading}</FeatureBox.Title>
                  <FeatureBox.Text>{description}</FeatureBox.Text>
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
          </StyledColumnTrack>
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const StyledColumnTrack = styled(ColumnTrack)`
  align-items: flex-start;
  row-gap: 60px;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    row-gap: 80px;
  }
`

const SectionCopy = styled(Section.Copy)`
  margin: 0 auto;
`

export default CommitmentToDataPrivacy
